/**
 * Retry an async operation with exponential backoff.
 *
 * Calls `fn` immediately. If it throws, retries up to `maxRetries` times
 * with the specified delays between attempts. Supports cancellation via
 * AbortSignal.
 *
 * @returns The resolved value from the first successful attempt.
 * @throws The error from the last failed attempt, or AbortError if aborted.
 */
export async function retryAsync<T>(
  fn: () => Promise<T>,
  options: {
    maxRetries: number;
    delays: number[];
    signal?: AbortSignal;
  }
): Promise<T> {
  const { maxRetries, delays, signal } = options;

  if (maxRetries > 0 && delays.length === 0) {
    throw new Error('retryAsync: delays must not be empty when maxRetries > 0');
  }

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) {
      throw new DOMException('Retry aborted', 'AbortError');
    }

    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      const delay = delays[attempt] ?? delays[delays.length - 1];
      await new Promise<void>((resolve, reject) => {
        let settled = false;

        const onAbort = () => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          reject(new DOMException('Retry aborted', 'AbortError'));
        };

        const onTimeout = () => {
          if (settled) return;
          settled = true;
          signal?.removeEventListener('abort', onAbort);
          resolve();
        };

        const timer = setTimeout(onTimeout, delay);

        if (signal) {
          signal.addEventListener('abort', onAbort, { once: true });
          // If abort() was called before the listener was registered,
          // the event already fired and won't fire again. Check after
          // registering to close the race window.
          if (signal.aborted) {
            onAbort();
          }
        }
      });
    }
  }

  // Unreachable — the loop either returns or throws
  throw new Error('retryAsync: unexpected state');
}
